// src/features/etf-ranking/api.ts
//
// Data fetcher for the Fixed-Income / ETF ranking page. Fixed-income
// sibling of features/stock-ranking/api.ts. Talks to the FastAPI
// etf-ranking router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the auth-gated /api/v1/etf-ranking router is reached as an
//     authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/etf-ranking/top-assets` path here —
// adding `/api/v1` would double the prefix. See api/client.ts
// (REQUIRED_API_PREFIX).

import { apiClient } from '../../api/client';

// Matches the exact JSON row shape from GET /api/v1/etf-ranking/top-assets
// and /universe. Mirrors the backend contract in
// app/core/cache_keys.py (_ETF_TOP_SIGNALS_ROW_KEYS) and the
// EtfDailyRanking ORM model exactly; keep them in lock-step. Diverges
// from the equity row: `category` (bond bucket) replaces `sector`,
// `value_z_score` replaces `fundamental_z_score`, and the composite is
// `forge_etf_signal`.
export interface EtfRankingRow {
    symbol: string;
    category: string;
    forge_etf_signal: number;
    annualized_volatility: number;
    momentum_z_score: number;
    value_z_score: number;
    sentiment_z_score: number;
    universe_rank: number;
    category_rank: number;
}

export interface TopAssetsResponse {
    as_of_date: string;
    top_n: number;
    rankings: EtfRankingRow[];
}

export const fetchTopAssets = async (): Promise<TopAssetsResponse> => {
    // Resolves to GET /api/v1/etf-ranking/top-assets (prefix from baseURL).
    const { data } = await apiClient.get<TopAssetsResponse>(
        '/etf-ranking/top-assets',
    );
    return data;
};

// --- Full universe (~200 ETFs, non-paginated) ---------------------------
// The backend returns the entire cross-section for the latest date in one
// payload. We filter + sort it entirely client-side, so category-tab and
// column-sort clicks never hit the network again.

// A universe row carries the same public columns as a leaderboard row.
export type UniverseRow = EtfRankingRow;

export interface UniverseResponse {
    as_of_date: string;
    total: number;
    items: UniverseRow[];
}

export const fetchUniverse = async (
    category?: string,
): Promise<UniverseResponse> => {
    // Resolves to GET /api/v1/etf-ranking/universe (prefix from baseURL).
    const { data } = await apiClient.get<UniverseResponse>(
        '/etf-ranking/universe',
        { params: category ? { category } : undefined },
    );
    return data;
};

// --- Batched historical trajectories (sparklines) ------------------------
// One request for many symbols collapses what would be an N+1 fetch
// waterfall (one call per visible table row) into a single round-trip.

export interface HistoryPoint {
    date: string;
    forge_etf_signal: number;
}

// Map of {symbol: [{date, forge_etf_signal}, ...]}. Every requested
// symbol is present as a key (empty array if it has no rows in window).
export type HistoricalBatchResponse = Record<string, HistoryPoint[]>;

export const fetchHistoricalBatch = async (
    symbols: string[],
    days = 90,
): Promise<HistoricalBatchResponse> => {
    if (symbols.length === 0) return {};
    const { data } = await apiClient.get<HistoricalBatchResponse>(
        '/etf-ranking/historical-batch',
        { params: { symbols: symbols.join(','), days } },
    );
    return data;
};

// --- Category summary (radar / heat strip) -------------------------------
// Per-category average factor z-scores, aggregated server-side over the
// latest date so the client receives ~a dozen rows instead of all 200.

export interface CategorySummaryRow {
    category: string;
    count: number;
    avg_momentum_z: number;
    avg_value_z: number;
    avg_sentiment_z: number;
    avg_signal: number;
}

export interface CategorySummaryResponse {
    as_of_date: string;
    categories: CategorySummaryRow[];
}

export const fetchCategorySummary =
    async (): Promise<CategorySummaryResponse> => {
        // Resolves to GET /api/v1/etf-ranking/category-summary.
        const { data } = await apiClient.get<CategorySummaryResponse>(
            '/etf-ranking/category-summary',
        );
        return data;
    };

// --- Quick stats (symbol pop-over) ---------------------------------------
// Headline ETF profile metrics for a single symbol. Powers the
// click-to-open symbol pop-over on the universe table. We go through the
// shared `apiClient` (NOT raw fetch) so the WP JWT is attached — the
// /etf-ranking router is auth-gated, so an unauthenticated fetch would 401.
//
// Every profile field is nullable: a freshly-listed ETF or one the v4
// etf-info enrichment never reached can have pricing but no profile row,
// in which case the backend returns null and the UI renders "N/A".

export interface QuickStats {
    symbol: string;
    company_name: string;
    category: string | null;
    expense_ratio: number | null;
    dividend_yield: number | null;
    assets_under_management: number | null;
    nav: number | null;
    last_price: number | null;
}

export const fetchQuickStats = async (
    symbol: string,
): Promise<QuickStats> => {
    // Resolves to GET /api/v1/etf-ranking/quick-stats/<symbol>.
    const { data } = await apiClient.get<QuickStats>(
        `/etf-ranking/quick-stats/${encodeURIComponent(symbol)}`,
    );
    return data;
};
